import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertTriangle,
  Plus,
  Search,
  Calendar,
  MapPin,
  User,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityOptions = [
  { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' },
];

const incidentTypes = [
  'Fall',
  'Medication Error',
  'Safeguarding',
  'Challenging Behaviour',
  'Near Miss',
  'Injury',
  'Equipment Failure',
  'Missing Person',
  'Abuse Allegation',
  'Other'
];

export default function Incidents() {
  const { user, organization } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({
    client_id: '',
    incident_type: '',
    description: '',
    severity: 'medium',
    location: '',
    witnesses: '',
    immediate_actions: '',
    incident_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, [organization, filterStatus]);

  const fetchData = async () => {
    try {
      const params = { organization_id: organization?.id };
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const [incidentsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/incidents`, { params }),
        axios.get(`${API_URL}/api/clients`, { params: { organization_id: organization?.id } })
      ]);
      
      setIncidents(incidentsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIncident = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/incidents`, {
        ...newIncident,
        organization_id: organization?.id,
        reported_by: user?.id,
        witnesses: newIncident.witnesses ? newIncident.witnesses.split(',').map(w => w.trim()) : [],
      });
      toast.success('Incident reported successfully');
      setIsDialogOpen(false);
      setNewIncident({
        client_id: '',
        incident_type: '',
        description: '',
        severity: 'medium',
        location: '',
        witnesses: '',
        immediate_actions: '',
        incident_date: new Date().toISOString().split('T')[0],
      });
      fetchData();
    } catch (error) {
      console.error('Error creating incident:', error);
      toast.error('Failed to report incident');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'N/A';
  };

  const getSeverityBadge = (severity) => {
    const option = severityOptions.find(s => s.value === severity);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option?.color || 'bg-slate-100'}`}>
        {option?.label || severity}
      </span>
    );
  };

  const filteredIncidents = incidents.filter(incident => {
    const clientName = getClientName(incident.client_id).toLowerCase();
    return clientName.includes(searchTerm.toLowerCase()) ||
           incident.incident_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           incident.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen" data-testid="incidents-page">
      <Header title="Incident Reports" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="incident-search"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-red-600 hover:bg-red-700" data-testid="report-incident-btn">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Report New Incident</DialogTitle>
                <DialogDescription>
                  Document the incident details. All incidents must be reported promptly.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateIncident} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="incident_type">Incident Type *</Label>
                    <Select 
                      value={newIncident.incident_type}
                      onValueChange={(v) => setNewIncident(prev => ({ ...prev, incident_type: v }))}
                    >
                      <SelectTrigger data-testid="incident-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {incidentTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="severity">Severity *</Label>
                    <Select 
                      value={newIncident.severity}
                      onValueChange={(v) => setNewIncident(prev => ({ ...prev, severity: v }))}
                    >
                      <SelectTrigger data-testid="incident-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {severityOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Involved Client</Label>
                    <Select 
                      value={newIncident.client_id}
                      onValueChange={(v) => setNewIncident(prev => ({ ...prev, client_id: v }))}
                    >
                      <SelectTrigger data-testid="incident-client">
                        <SelectValue placeholder="Select client (if applicable)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.first_name} {client.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="incident_date">Date & Time *</Label>
                    <Input
                      id="incident_date"
                      type="datetime-local"
                      value={newIncident.incident_date}
                      onChange={(e) => setNewIncident(prev => ({ ...prev, incident_date: e.target.value }))}
                      required
                      data-testid="incident-date"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={newIncident.location}
                    onChange={(e) => setNewIncident(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Where did the incident occur?"
                    required
                    data-testid="incident-location"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={newIncident.description}
                    onChange={(e) => setNewIncident(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Describe what happened in detail..."
                    required
                    data-testid="incident-description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="immediate_actions">Immediate Actions Taken *</Label>
                  <Textarea
                    id="immediate_actions"
                    value={newIncident.immediate_actions}
                    onChange={(e) => setNewIncident(prev => ({ ...prev, immediate_actions: e.target.value }))}
                    rows={3}
                    placeholder="What actions were taken immediately after the incident?"
                    required
                    data-testid="incident-actions"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="witnesses">Witnesses</Label>
                  <Input
                    id="witnesses"
                    value={newIncident.witnesses}
                    onChange={(e) => setNewIncident(prev => ({ ...prev, witnesses: e.target.value }))}
                    placeholder="Names separated by commas"
                    data-testid="incident-witnesses"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="submit-incident">
                    Submit Report
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Open Incidents</p>
                  <p className="text-3xl font-bold text-red-600">
                    {incidents.filter(i => i.status === 'open').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Investigating</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {incidents.filter(i => i.status === 'investigating').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Closed</p>
                  <p className="text-3xl font-bold text-green-600">
                    {incidents.filter(i => i.status === 'closed').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total This Month</p>
                  <p className="text-3xl font-bold">{incidents.length}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Incidents List */}
        <Card data-testid="incidents-list">
          <CardHeader>
            <CardTitle>All Incident Reports</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredIncidents.length > 0 ? (
              <div className="space-y-4">
                {filteredIncidents.map((incident) => (
                  <div 
                    key={incident.id}
                    className="p-4 bg-slate-50 rounded-lg border-l-4 border-l-red-400"
                    data-testid={`incident-${incident.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium">{incident.incident_type}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(incident.incident_date).toLocaleString('en-GB')}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getSeverityBadge(incident.severity)}
                        <Badge variant={incident.status === 'open' ? 'destructive' : incident.status === 'investigating' ? 'default' : 'secondary'}>
                          {incident.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-slate-700 mb-3">{incident.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {incident.location}
                      </span>
                      {incident.client_id && (
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {getClientName(incident.client_id)}
                        </span>
                      )}
                      {incident.cqc_notified && (
                        <Badge variant="outline" className="text-xs">CQC Notified</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No incidents found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
